import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Nav, Navbar } from "react-bootstrap";
import NavDropdown from "react-bootstrap/NavDropdown";
import * as permissions from "./app_props";
import "./styles.css";
import { User } from "lucide-react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  IconButton,
  Popover,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Badge,
  Stack,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { headers, restApiUrl } from "./app_props";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const Navigation = () => {
  const user = JSON.parse(sessionStorage.getItem("userInfo"));
  const header = headers();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const popoverRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [badgeCount, setBadgeCount] = useState(0);

  useEffect(() => {
    fetchNotificationBadgeCount();
  }, []);

  const fetchNotificationBadgeCount = async () => {
    if (!user?.username) {
      return;
    }
    const response = await fetch(
      restApiUrl + `/notification/badgecount/${user.username}`,
      {
        method: "GET",
        mode: "cors",
        headers: header,
      },
    );
    if (!response.ok) throw new Error("Failed to fetch notifications");

    const data = await response.json();
    setBadgeCount(data.badgecount);
  };

  const fetchNotifications = async () => {
    if (loading || !hasMore) return;

    setLoading(true);

    try {
      const response = await fetch(
        restApiUrl + `/notification/${user.username}?page=${page - 1}`,
        {
          method: "GET",
          mode: "cors",
          headers: header,
        },
      );
      if (!response.ok) throw new Error("Failed to fetch notifications");

      const data = await response.json();

      setNotifications(
        data.totalPages < page
          ? (prev) => [...prev, ...data.notifications]
          : data.notifications,
      ); // Append new notifications
      setHasMore(data.notifications.length > 0); // If API returns empty, stop fetching
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (popoverRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = popoverRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 10 && !loading) {
          setPage((prev) => prev + 1);
          fetchNotifications();
        }
      }
    };

    const popoverElement = popoverRef.current;
    if (popoverElement) {
      popoverElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (popoverElement) {
        popoverElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [loading, hasMore]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case "ALERT":
        return <WarningIcon color="error" sx={{ marginRight: 1 }} />;
      case "INFO":
        return <InfoIcon color="primary" sx={{ marginRight: 1 }} />;
      case "SUCCESS":
        return <CheckCircleIcon color="success" sx={{ marginRight: 1 }} />;
      default:
        return <InfoIcon sx={{ marginRight: 1 }} />;
    }
  };

  const handleClick = (event) => {
    fetchNotifications();
    // setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const getNotificationStyle = (type, isRead) => {
    if (isRead) {
      return { backgroundColor: "#f0f0f0", borderLeft: "5px solid #bdbdbd" }; // Light grey for read notifications
    }
    switch (type) {
      case "ALERT":
        return { backgroundColor: "#ffebee", borderLeft: "5px solid #d32f2f" }; // Light red with dark red border
      case "WARNING":
        return { backgroundColor: "#fff3e0", borderLeft: "5px solid #ff9800" }; // Light orange with dark orange border
      case "INFO":
        return { backgroundColor: "#e3f2fd", borderLeft: "5px solid #1976d2" }; // Light blue with dark blue border
      default:
        return { backgroundColor: "#f5f5f5", borderLeft: "5px solid #9e9e9e" }; // Default grey
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    setOpen(false);
    const notificationUserId = notifications
      .find((notification) => notification.id === notificationId)
      ?.notificationUsers.find(
        (nuser) => nuser.user.username === user.username,
      )?.id;
    try {
      header.delete("Content-Type");
      const response = await fetch(
        restApiUrl + `/notification/markAsRead/${notificationUserId}`,
        {
          method: "PUT",
          mode: "cors",
          headers: header,
        },
      );

      if (!response.ok) throw new Error("Failed to mark notification as read");
      const updatedNotifications = notifications.map((notification) =>
        notification.id === notificationId
          ? {
            ...notification,
            notificationUsers: notification.notificationUsers.map((nuser) =>
              nuser.user.username === user.username
                ? { ...nuser, read: true }
                : nuser,
            ),
          }
          : notification,
      );
      setNotifications(updatedNotifications);
      setBadgeCount(badgeCount - 1);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleOnClickLink = async (notificationId) => {
    handleMarkAsRead(notificationId);
  };

  return (
    <Navbar expand="lg" variant="dark" style={{ backgroundColor: "#225d23" }}>
      <Navbar.Toggle
        aria-controls="basic-navbar-nav"
        className="navbar-dark" />
      <Navbar.Collapse id="basic-navbar-nav">
        <Nav className="mr-auto">
          <Nav.Link
            as={Link}
            to={permissions.comatHome}
            style={{
              color:
                location.pathname === permissions.comatHome
                  ? "#ff6600"
                  : "#ffff",
            }}
          >
            Comat Home
          </Nav.Link>
          <Nav.Link
            as={Link}
            to="/dashboard"
            style={{
              color: location.pathname === "/dashboard" ? "#ff6600" : "#ffff",
            }}
          >
            Dashboard
          </Nav.Link>
          {user && (
            <Nav.Link
              as={Link}
              to="/permits"
              style={{
                color: location.pathname === "/permits" ? "#ff6600" : "#ffff",
              }}
            >
              Permits
            </Nav.Link>
          )}
          {/* {user && (
            <Nav.Link
              as={Link}
              to="/permits/new"
              style={{
                color:
                  location.pathname === "/permits/new" ? "#ff6600" : "#ffff",
              }}
            >
              New Permit
            </Nav.Link>
          )} */}
          {user && (
            <Nav.Link
              as={Link}
              to="/admin/master-data"
              style={{
                color:
                  location.pathname === "/admin/master-data"
                    ? "#ff6600"
                    : "#ffff",
              }}
            >
              Master Data
            </Nav.Link>
          )}
          {user && (
            <Nav.Link
              as={Link}
              to="/admin/contractors"
              style={{
                color:
                  location.pathname === "/admin/contractors"
                    ? "#ff6600"
                    : "#ffff",
              }}
            >
              Contractors
            </Nav.Link>
          )}
        </Nav>
      </Navbar.Collapse>
      <div
        style={{
          display: "flex",
          zIndex: 2000,
          alignItems: "center",
        }}
      >
        <div>
          <IconButton color="inherit" onClick={() => handleClick()}>
            <Badge badgeContent={badgeCount} color="error">
              <NotificationsIcon sx={{ color: "white" }} />
            </Badge>
          </IconButton>
          <Popover
            open={open}
            anchorEl={anchorEl}
            // onClick={handleClick}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  width: 350,
                  maxHeight: 400,
                  overflowY: "auto",
                  top: "40px !important",
                },
              },
            }}
          >
            <div
              ref={popoverRef}
              style={{
                padding: "8px !important",
                maxHeight: 380,
                overflowY: "auto",
              }}
            >
              {notifications.map((record, index) => {
                const currentUserStatus = record.notificationUsers.find(
                  (userObj) => userObj.user.username === user.username,
                );
                const isRead = currentUserStatus
                  ? currentUserStatus.read
                  : false;
                return (
                  <Card
                    key={index}
                    sx={{
                      margin: 1,
                      ...getNotificationStyle(record.type, isRead),
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" alignItems="center">
                        {getNotificationIcon(record.type)}
                        <Typography
                          component={Link}
                          to={record.link}
                          onClick={
                            isRead
                              ? undefined
                              : () => handleOnClickLink(record.id)
                          }
                          sx={{
                            textDecoration: "none",
                            color: "inherit",
                            fontSize: "0.850rem",
                            lineHeight: "1",
                          }}
                        >
                          {record.message}
                        </Typography>
                        <IconButton
                          onClick={
                            isRead
                              ? undefined
                              : () => handleMarkAsRead(record.id)
                          }
                          sx={{ padding: 0 }}
                          disabled={isRead}
                        >
                          <VisibilityIcon
                            disabled={isRead}
                            sx={{
                              fontSize: 18,
                              color: isRead ? "#9e9e9e" : "#1976d2",
                            }}
                          />
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading && (
                <CircularProgress
                  style={{ display: "block", margin: "auto" }}
                />
              )}
            </div>
          </Popover>
        </div>
        <NavDropdown
          title={
            <div
              style={{
                color: "#ffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px", // Add consistent spacing between elements
              }}
            >
              <User size={16} />
              <span>{user ? user.username : "User"}</span>
            </div>
          }
          id="basic-nav-dropdown"
          align="end"
          style={{ padding: 0 }}
        >
          {" "}
          <NavDropdown.Item as={Link} to="/logout">
            Logout
          </NavDropdown.Item>
        </NavDropdown>
      </div>
    </Navbar>
  );
};

export default Navigation;
